import { Box, Button, ClickAwayListener, MenuItem, MenuList, Paper, Popper, Select, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CustomSearchBar from '../../common/CustomSearchBar'
import { debounce, Icons } from '../../../utils/utilities'
import { useSearchParams } from 'react-router-dom'
import { useUserStore } from '../../../store/user.store'

const encodeFilter = ( { field, value } ) => `field:${field},value:${value}`

const SessionsFilterAndSortBar = () => {

    const [subjectsOptionsAnchorEl, setSubjectsOptionsAnchorEl] = useState( null )
    const [acYearsOptionsAnchorEl, setAcYearsOptionsAnchorEl] = useState( null )
    const [filterOptionOpen, setFilterOptionOpen] = useState( false )
    const [sortOptionOpen, setSortOptionOpen] = useState( false )
    const [subFilter, setSubFilter] = useState( "" )

    const { zIndex, border, palette } = useTheme()

    const [searchParams, setSearchParams] = useSearchParams( { sort: "latestfirst" } )
    const searchText = searchParams.get( 'searchText' )
    const sort = searchParams.get( 'sort' )
    const filters = searchParams.getAll( "filter" ).map( item => {
        const obj = Object.fromEntries(
            item.split( "," ).map( p => p.split( ":" ) )
        )
        return { field: obj.field, value: obj.value }
    } )

    const UserStore = useUserStore()

    const filterBtnRef = useRef()
    const sortBtnRef = useRef()

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const setSearchText = ( value ) => setSearchParams( { searchText: value || "" } )

    const handleClickAwayOnFilterPopper = () => {
        setSubjectsOptionsAnchorEl( null )
        setAcYearsOptionsAnchorEl( null )
        setFilterOptionOpen( false )
    }

    const removeFilters = () => {
        setSearchParamsValues( { filter: '' } )
    }

    const handleFilterRequest = ( action = "set", data ) => {
        let newFilter = [...filters]

        if ( action === "set" ) {
            newFilter.push( data )
        } else if ( action === "remove" ) {
            newFilter = newFilter.filter( f => f.field !== data )
        } else if ( action === "update" ) {
            const { index, update } = data
            const value = { ...newFilter[index], ...update }
            newFilter.splice( index, 1, value )
        }


        setSearchParams( prev => {
            const params = new URLSearchParams( prev )
            params.delete( "filter" ) // clear old entries
            newFilter.forEach( f => params.append( "filter", encodeFilter( f ) ) )
            return params
        }, { replace: true } )
    }

    const filtersAndKeyMap = useMemo( () => {
        if ( !Array.isArray( filters ) ) return {}

        return filters.reduce( ( acc, { field, value } ) => {
            acc[field] = value
            return acc
        }, {} )
    }, [filters] )

    return (
        <Box display="flex" justifyContent="space-between" gap="10px" flexWrap="wrap">

            <CustomSearchBar value={searchText || ""} size='small' placeholder='Search by title, section or batch name...' style={{ flexGrow: 1 }} onChange={( event ) => setSearchText( ( event.target.value || "" ).toLowerCase() )} />

            <Button onClick={() => setSortOptionOpen( true )} variant='outlined' ref={sortBtnRef} color='secondary' disableElevation startIcon={Icons.default.Sort2} size='small' >Sort</Button>
            <Popper placement='bottom-end' anchorEl={sortBtnRef.current} open={sortOptionOpen}>
                <ClickAwayListener onClickAway={() => setSortOptionOpen( false )}>
                    <Paper sx={{ paddingY: "2px", marginY: "5px", border: border[1] }}>
                        <MenuList dense>
                            <MenuItem dense onClick={() => setSearchParams( { sort: "latestfirst" } )} selected={sort === "latestfirst"}>Latest first</MenuItem>
                            <MenuItem dense onClick={() => setSearchParams( { sort: "oldestfirst" } )} selected={sort === "oldestfirst"}>Oldest first</MenuItem>
                            <MenuItem dense onClick={() => setSearchParams( { sort: "alphaasc" } )} selected={sort === "alphaasc"}>Title: A-Z</MenuItem>
                            <MenuItem dense onClick={() => setSearchParams( { sort: "alphadesc" } )} selected={sort === "alphadesc"}>Title: Z-A</MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>


            <Button variant='outlined' color='secondary' ref={filterBtnRef} onClick={() => setFilterOptionOpen( true )} disableElevation startIcon={Icons.default.FilterAlt} size='small' >Filter</Button>
            <Popper placement='bottom-end' anchorEl={filterBtnRef.current} open={filterOptionOpen}>
                <ClickAwayListener onClickAway={handleClickAwayOnFilterPopper}>
                    <Paper sx={{ width: "90vw", maxWidth: "300px", paddingY: "2px", marginY: "5px", border: border[1] }}>
                        <Box display="flex" flexDirection="column" padding="10px">
                            <Typography gutterBottom display="block" variant='subtitle2' >Filters ({filters.length})</Typography>

                            <Box marginTop="10px">
                                <Typography display="block" variant='subtitle2' >Academic year</Typography>
                                <Button fullWidth onClick={( event ) => setAcYearsOptionsAnchorEl( event.currentTarget )} sx={{ justifyContent: "space-between", fontWeight: "normal" }} size='small' variant='outlined' color='greyed' endIcon={Icons.default.DropDown} >
                                    {filtersAndKeyMap.acYear || "Not applied"}
                                </Button>
                                <Popper sx={{ zIndex: zIndex.drawer * 2 }} open={Boolean( acYearsOptionsAnchorEl )} anchorEl={acYearsOptionsAnchorEl} placement='bottom-start' >
                                    <ClickAwayListener onClickAway={() => setAcYearsOptionsAnchorEl( null )}>
                                        <Paper sx={{ overflow: "auto", maxHeight: "200px", minWidth: 200, border: border[1], marginTop: "5px" }}>
                                            <MenuList>
                                                {UserStore.getUserSubjectsDetails.academicYears?.sort( ( a, b ) => a > b ? -1 : 1 ).map( ( filter, i ) => (
                                                    <MenuItem onClick={() => {
                                                        handleFilterRequest( filtersAndKeyMap.acYear ? "update" : "set", filtersAndKeyMap.acYear ? { index: filters.findIndex( fil => fil.field === "acYear" ), update: { field: 'acYear', value: filter } } : { field: 'acYear', value: filter } )
                                                        setAcYearsOptionsAnchorEl( null )
                                                    }} dense sx={{ fontSize: "14px" }} value={filter} key={filter} >
                                                        {UserStore.getUser.user_role === "STUDENT" ? UserStore.getUserSubjectsDetails.yearWise ? "Year" : "Semester" : "Academic Year"} {filter}
                                                    </MenuItem>
                                                ) )}
                                                {filtersAndKeyMap.acYear && <MenuItem
                                                    onClick={() => {
                                                        handleFilterRequest( "remove", "acYear" )
                                                        setAcYearsOptionsAnchorEl( null )
                                                    }}
                                                    dense
                                                    sx={{
                                                        fontSize: "14px",
                                                        bgcolor: palette.error.main,
                                                        color: palette.error.contrastText,
                                                        gap: "10px",
                                                        justifyContent: "center",
                                                        ":hover": {
                                                            bgcolor: palette.error.dark,
                                                            color: palette.error.contrastText,
                                                        }
                                                    }}
                                                    value={"remove"}
                                                    key={"remove"}
                                                >
                                                    {Icons.default.CloseIcon}
                                                    Remove this filter
                                                </MenuItem>}
                                            </MenuList>
                                        </Paper>
                                    </ClickAwayListener>
                                </Popper>

                            </Box>

                            <Box marginTop="10px">
                                <Typography display="block" variant='subtitle2' >Subject</Typography>

                                <Button fullWidth onClick={( event ) => setSubjectsOptionsAnchorEl( event.currentTarget )} sx={{ justifyContent: "space-between", fontWeight: "normal" }} size='small' variant='outlined' color='greyed' endIcon={Icons.default.DropDown} >
                                    {filtersAndKeyMap.subject ? UserStore.getUserSubjectsDetails.currentSubjects.find( sub => sub.subject_code === filtersAndKeyMap.subject )?.subject_name : "Not applied"}
                                </Button>
                                <Popper sx={{ zIndex: zIndex.drawer * 2 }} open={Boolean( subjectsOptionsAnchorEl )} anchorEl={subjectsOptionsAnchorEl} placement='bottom-start' >
                                    <ClickAwayListener onClickAway={() => setSubjectsOptionsAnchorEl( null )}>
                                        <Paper sx={{ overflow: "auto", maxHeight: "200px", minWidth: 200, border: border[1], marginTop: "5px" }}>
                                            <MenuList>
                                                {UserStore.getUserSubjectsDetails.currentSubjects?.filter( sub => !filtersAndKeyMap.acYear || sub.ac_year === filtersAndKeyMap.acYear ).map( ( filter, i ) => (
                                                    <MenuItem onClick={() => {
                                                        handleFilterRequest( filtersAndKeyMap.subject ? "update" : "set", filtersAndKeyMap.subject ? { index: filters.findIndex( fil => fil.field === "subject" ), update: { field: 'subject', value: filter.subject_code } } : { field: 'subject', value: filter.subject_code } )
                                                        setSubjectsOptionsAnchorEl( null )
                                                    }} dense sx={{ fontSize: "14px" }} selected={filtersAndKeyMap?.subject === filter.subject_code} value={filter.subject_code} key={filter.subject_code} >
                                                        {filter.subject_name}
                                                    </MenuItem>
                                                ) )}
                                                {filtersAndKeyMap.subject && <MenuItem
                                                    onClick={() => {
                                                        handleFilterRequest( "remove", "subject" )
                                                        setSubjectsOptionsAnchorEl( null )
                                                    }}
                                                    dense
                                                    sx={{
                                                        fontSize: "14px",
                                                        bgcolor: palette.error.main,
                                                        color: palette.error.contrastText,
                                                        gap: "10px",
                                                        justifyContent: "center",
                                                        ":hover": {
                                                            bgcolor: palette.error.dark,
                                                            color: palette.error.contrastText,
                                                        }
                                                    }}
                                                    value={"remove"}
                                                    key={"remove"}
                                                >
                                                    {Icons.default.CloseIcon}
                                                    Remove this filter
                                                </MenuItem>}
                                            </MenuList>
                                        </Paper>
                                    </ClickAwayListener>
                                </Popper>

                            </Box>

                            <Box marginTop="20px">
                                <Button onClick={removeFilters} size='small' variant='contained' disableElevation color='error' startIcon={Icons.default.FilterAltOff}>Remove Filters</Button>
                            </Box>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>

        </Box>
    )
}

export default SessionsFilterAndSortBar

{/* {tabValue === 'materials' && <Button onClick={() => refUploadInputRef.current?.click()} size='small' startIcon={Icons.default.UploadFileIcon} variant='contained' disableElevation >Upload reference materials</Button>} */ }

